import { useEffect, useRef } from "react";
import { connectSocket, socket } from "../../socket";
import { forceLogout } from "../../Services/auth/session.service";
import { SESSION_EXPIRED_MESSAGE } from "../../Constants/messages";

// The server rejects a bad or expired handshake token with "Authentication
// error"; anything else (server down, transport failure) must not log out.
const isAuthHandshakeFailure = (error: Error) =>
  /auth|token|jwt|unauthor/i.test(error?.message ?? "");

export const useSocket = () => {
  const socketRef = useRef<typeof socket | null>(null);

  useEffect(() => {
    if (socketRef.current) return;
    socketRef.current = socket;

    const onConnect = () => console.info("Socket connected");
    const onDisconnect = () => console.info("Socket disconnected");
    const onConnectError = (error: Error) => {
      if (isAuthHandshakeFailure(error)) {
        forceLogout({ message: SESSION_EXPIRED_MESSAGE });
      }
    };

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("connect_error", onConnectError);

    connectSocket();

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("connect_error", onConnectError);
    };
  }, []);

  return socketRef.current;
};

export const useSocketListener = ({
  eventName,
  handlerFunction,
}: {
  eventName: string;
  handlerFunction: (data: any) => unknown;
}) => {
  useEffect(() => {
    socket.on(eventName, handlerFunction);

    return () => {
      socket.off(eventName, handlerFunction);
    };
  }, [eventName, handlerFunction]);
};
